package nacholab.supplies.pages.mainscreen

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.dom.observers.ResizeObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.shapes.Rect
import com.varabyte.kobweb.silk.theme.shapes.clip
import nacholab.supplies.MainViewModel
import nacholab.supplies.components.*
import nacholab.supplies.i18n.i18n
import nacholab.supplies.ui.theme.CommonStyles
import nacholab.supplies.ui.theme.SuppliesTheme
import org.jetbrains.compose.web.css.AlignSelf
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.Element

@Composable
fun MainScreen(mainViewModel: MainViewModel){
    val state by mainViewModel.state
    var suppliesTableElement by remember { mutableStateOf<Element?>(null) }
    var suppliesTableWidth by remember { mutableStateOf(0.0) }

    LaunchedEffect(suppliesTableElement) {
        suppliesTableElement?.let {
            ResizeObserver { elements ->
                suppliesTableWidth = elements.firstOrNull()?.contentRect?.width?:0.0
            }.observe(it)
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(SuppliesTheme.colors.background)
                .padding(top = 12.px)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(24.px),
                modifier = Modifier.padding(leftRight = 12.px)
            ) {
                Row {
                    Image(
                        src = "/ic_title.png",
                        modifier = Modifier.size(32.px)
                    )

                    Box(Modifier.size(12.px))

                    SpanText(
                        text = "appTitle".i18n(state.lang),
                        modifier = Modifier
                            .fontSize(22.px)
                            .color(SuppliesTheme.colors.primary)
                    )

                    Box(Modifier.size(24.px))
                }

                if (state.suppliesLoading) NLLoading() else FaRotate(
                    size = IconSize.XL,
                    modifier = Modifier
                        .color(SuppliesTheme.colors.primary)
                        .onClick { mainViewModel.reloadMySupplies() }
                        .then(CommonStyles.hoverPointer.toModifier())
                )

                when {
                    state.savingEdit -> NLLoading()
                    state.editingMode -> {
                        Box {
                            FaPencil(
                                size = IconSize.XL,
                                modifier = Modifier
                                    .color(SuppliesTheme.colors.primary)
                            )
                            FaBan(
                                size = IconSize.XL,
                                modifier = Modifier
                                    .color(SuppliesTheme.colors.primary)
                                    .onClick { mainViewModel.toggleEditMode() }
                                    .then(CommonStyles.hoverPointer.toModifier())
                            )
                        }
                    }

                    else -> FaPencil(
                        size = IconSize.XL,
                        modifier = Modifier
                            .color(SuppliesTheme.colors.primary)
                            .onClick { mainViewModel.toggleEditMode() }
                            .then(CommonStyles.hoverPointer.toModifier())
                    )
                }

                FaPlus(
                    size = IconSize.XL,
                    modifier = Modifier
                        .color(SuppliesTheme.colors.primary)
                        .onClick { mainViewModel.initAddSupply() }
                        .then(CommonStyles.hoverPointer.toModifier())
                )
            }

            Box(modifier = Modifier.size(48.px))

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .overflow(Overflow.Auto)
            ) {
                Table(
                    Modifier
                        .width(suppliesTableWidth.px)
                        .background(SuppliesTheme.colors.utilityTableBorder)
                        .position(Position.Fixed)
                        .zIndex(100)
                        .toAttrs()
                ) {
                    Tr(attrs = Modifier.height(28.px).toAttrs()) {
                        Th(
                            attrs = Modifier
                                .background(SuppliesTheme.colors.primary)
                                .width(40f.percent)
                                .attrs("rowspan" to "2")
                                .toAttrs()
                        ) {
                            CellText(
                                text = "supplylist_name".i18n(state.lang),
                                modifier = Modifier
                                    .onClick { mainViewModel.sortByName() }
                                    .then(CommonStyles.hoverPointer.toModifier())
                            )
                        }
                        Th(
                            attrs = Modifier
                                .background(SuppliesTheme.colors.primary)
                                .width(20f.percent)
                                .attrs("colspan" to "2")
                                .toAttrs()
                        ) {
                            CellText("Stock")
                        }
                        Th(
                            attrs = Modifier
                                .background(SuppliesTheme.colors.primary)
                                .width(40f.percent)
                                .attrs("colspan" to "2")
                                .toAttrs()
                        ) {
                            CellText(
                                text = "supplylist_location".i18n(state.lang),
                                modifier = Modifier
                                    .onClick { mainViewModel.sortByHome() }
                                    .then(CommonStyles.hoverPointer.toModifier())
                            )
                        }
                    }
                    Tr(attrs = Modifier.height(28.px).toAttrs()) {
                        Th(
                            attrs = Modifier
                                .background(SuppliesTheme.colors.primary)
                                .width(10f.percent)
                                .toAttrs()
                        ) {
                            CellText(text = "supplylist_current".i18n(state.lang))
                        }
                        Th(
                            attrs = Modifier
                                .background(SuppliesTheme.colors.primary)
                                .width(10f.percent)
                                .toAttrs()
                        ) {
                            CellText(text = "supplylist_required".i18n(state.lang))
                        }
                        Th(
                            attrs = Modifier
                                .background(SuppliesTheme.colors.primary)
                                .width(20f.percent)
                                .toAttrs()
                        ) {
                            CellText(
                                text = "supplylist_home".i18n(state.lang),
                                modifier = Modifier
                                    .onClick { mainViewModel.sortByHome() }
                                    .then(CommonStyles.hoverPointer.toModifier())
                            )
                        }
                        Th(
                            attrs = Modifier
                                .background(SuppliesTheme.colors.primary)
                                .width(20f.percent)
                                .toAttrs()
                        ) {
                            CellText(
                                text = "supplylist_market".i18n(state.lang),
                                modifier = Modifier
                                    .onClick { mainViewModel.sortByMarket() }
                                    .then(CommonStyles.hoverPointer.toModifier())
                            )
                        }
                    }
                }

                Table(
                    Modifier
                        .fillMaxWidth()
                        .background(SuppliesTheme.colors.utilityTableBorder)
                        .padding(top = 60.px)
                        .toAttrs {
                            ref { elm ->
                                suppliesTableElement = elm
                                onDispose { suppliesTableElement = null }
                            }
                        }
                ) {
                    state.supplies.map {
                        Tr(attrs = Modifier.height(28.px).toAttrs()) {
                            Td(
                                attrs = Modifier
                                    .background(SuppliesTheme.colors.primary)
                                    .width(40f.percent)
                                    .toAttrs()
                            ) {
                                if (state.editingMode) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {

                                        Box(Modifier.size(12.px))

                                        FaX(
                                            size = IconSize.SM,
                                            modifier = Modifier
                                                .color(SuppliesTheme.colors.utilityError)
                                                .then(CommonStyles.hoverPointer.toModifier())
                                                .onClick { _ -> mainViewModel.deleteConsumable(it.id) }
                                        )

                                        Box(Modifier.size(12.px))

                                        CellTextInput(
                                            value = state.nameFields[it.id],
                                            onInput = { i -> mainViewModel.updateNameField(it.id, i) },
                                            onFocusIn = { mainViewModel.startEditingName(it.id) },
                                            onFocusOut = { mainViewModel.endEditingName(it.id) },
                                            modifier = Modifier.weight(1f)
                                        )
                                    }
                                } else CellText(it.name)
                            }
                            Td(
                                attrs = Modifier
                                    .background(SuppliesTheme.colors.primary)
                                    .width(10f.percent)
                                    .toAttrs()
                            ) {
                                if (state.editingMode) CellNumberInput(
                                    value = state.currentStockFields[it.id],
                                    onChange = { v -> mainViewModel.updateCurrentStockField(it.id, v) },
                                    modifier = Modifier.fillMaxWidth().textAlign(TextAlign.Center)
                                ) else Box(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentAlignment = Alignment.Center
                                ) {
                                    CellText(it.currentStock.toString())
                                }
                            }
                            Td(
                                attrs = Modifier
                                    .background(SuppliesTheme.colors.primary)
                                    .width(10f.percent)
                                    .toAttrs()
                            ) {
                                if (state.editingMode) CellNumberInput(
                                    value = state.requiredStockFields[it.id],
                                    onChange = { v -> mainViewModel.updateRequiredStockField(it.id, v) },
                                    modifier = Modifier.fillMaxWidth().textAlign(TextAlign.Center)
                                ) else Box(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentAlignment = Alignment.Center
                                ) {
                                    CellText(it.requiredStock.toString())
                                }
                            }
                            Td(
                                attrs = Modifier
                                    .background(SuppliesTheme.colors.primary)
                                    .width(20f.percent)
                                    .toAttrs()
                            ) {
                                if (state.editingMode) CellTextInput(
                                    value = state.homeFields[it.id],
                                    onInput = { i -> mainViewModel.updateHomeField(it.id, i) },
                                    onFocusIn = { mainViewModel.startEditingHome(it.id) },
                                    onFocusOut = { mainViewModel.endEditingHome(it.id) },
                                    modifier = Modifier.fillMaxWidth().textAlign(TextAlign.Center)
                                ) else Box(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentAlignment = Alignment.Center
                                ) {
                                    CellText(it.locationAtHome)
                                }
                            }
                            Td(
                                attrs = Modifier
                                    .background(SuppliesTheme.colors.primary)
                                    .width(20f.percent)
                                    .toAttrs()
                            ) {
                                if (state.editingMode) CellTextInput(
                                    value = state.marketFields[it.id],
                                    onInput = { i -> mainViewModel.updateMarketField(it.id, i) },
                                    onFocusIn = { mainViewModel.startEditingMarket(it.id) },
                                    onFocusOut = { mainViewModel.endEditingMarket(it.id) },
                                    modifier = Modifier.fillMaxWidth().textAlign(TextAlign.Center)
                                ) else Box(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentAlignment = Alignment.Center
                                ) {
                                    CellText(it.locationAtMarket)
                                }
                            }
                        }
                    }
                }
            }
        }

        if (state.showAddSupplyDialog) Box(
            modifier = Modifier
                .fillMaxSize()
                .background(SuppliesTheme.colors.utilityBackdrop)
                .zIndex(200),
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .background(SuppliesTheme.colors.background)
                    .clip(Rect(cornerRadius = 18.px))
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(24.px)
                ) {
                    SpanText(
                        text = "addsupply_title".i18n(state.lang),
                        modifier = Modifier
                            .fontSize(30.px)
                            .fillMaxWidth()
                            .textAlign(TextAlign.End)
                            .color(SuppliesTheme.colors.primary)
                            .fontWeight(FontWeight.Bold)
                    )

                    Box(modifier = Modifier.size(24.px))

                    SpanText(
                        text = "addsupply_name".i18n(state.lang),
                        modifier = Modifier
                            .fontSize(20.px)
                            .color(SuppliesTheme.colors.primary)
                            .fontWeight(FontWeight.Bold)
                    )

                    NLTextInput(
                        value = state.addSupplyName,
                        onChange = { mainViewModel.updateCreatingSupplyName(it) },
                        modifier = Modifier.fillMaxWidth()
                    )

                    Box(modifier = Modifier.size(12.px))

                    SpanText(
                        text = "addsupply_homeLocation".i18n(state.lang),
                        modifier = Modifier
                            .fontSize(20.px)
                            .color(SuppliesTheme.colors.primary)
                            .fontWeight(FontWeight.Bold)
                    )

                    NLTextInput(
                        value = state.addSupplyLocationAtHome,
                        onChange = { mainViewModel.updateCreatingSupplyLocationAtHome(it) },
                        modifier = Modifier.fillMaxWidth()
                    )

                    Box(modifier = Modifier.size(12.px))

                    SpanText(
                        text = "addsupply_marketLocation".i18n(state.lang),
                        modifier = Modifier
                            .fontSize(20.px)
                            .color(SuppliesTheme.colors.primary)
                            .fontWeight(FontWeight.Bold)
                    )

                    NLTextInput(
                        value = state.addSupplyLocationAtMarket,
                        onChange = { mainViewModel.updateCreatingSupplyLocationAtMarket(it) },
                        modifier = Modifier.fillMaxWidth()
                    )

                    Box(modifier = Modifier.size(12.px))

                    SpanText(
                        text = "addsupply_stock".i18n(state.lang),
                        modifier = Modifier
                            .fontSize(20.px)
                            .color(SuppliesTheme.colors.primary)
                            .fontWeight(FontWeight.Bold)
                    )

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.px),
                        verticalAlignment = Alignment.CenterVertically
                    ) {

                        NLNumberInput(
                            value = state.addSupplyCurrentStock,
                            onChange = { mainViewModel.updateCreatingSupplyCurrentStock(it) },
                            modifier = Modifier.fillMaxWidth().textAlign(TextAlign.Center).weight(1f)
                        )

                        SpanText(
                            text = "/",
                            modifier = Modifier
                                .fontSize(20.px)
                                .color(SuppliesTheme.colors.primary)
                                .fontWeight(FontWeight.Bold)
                        )

                        NLNumberInput(
                            value = state.addSupplyRequiredStock,
                            onChange = { mainViewModel.updateCreatingSupplyRequiredStock(it) },
                            modifier = Modifier.fillMaxWidth().textAlign(TextAlign.Center).weight(1f)
                        )
                    }

                    Box(modifier = Modifier.size(24.px))

                    Row(
                        modifier = Modifier.fillMaxWidth().height(56.px),
                        horizontalArrangement = Arrangement.spacedBy(24.px),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (state.savingEdit)Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                            NLLoading(IconSize.XL)
                        }else{
                            NLButton(
                                text = "addsupply_cancel".i18n(state.lang),
                                onClick = { mainViewModel.cancelAddSupply() },
                                modifier = Modifier.weight(1f)
                            )
                            NLButton(
                                text = "addsupply_save".i18n(state.lang),
                                onClick = { mainViewModel.saveCurrentlyAddingSupply() },
                                modifier = Modifier.weight(1f)
                            )
                        }
                    }
                }
            }
        }
    }
}